import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Rows3, ListOrdered, CalendarDays, AlertTriangle, Truck, UserCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { scheduleApi } from "@/lib/tourEntities";
import { assetApi, customerApi, driverApi, driverAvailabilityApi } from "@/lib/entities";
import { paginatedFetch, wrapListApi } from "@/lib/paginatedFetch";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { RoleGate } from "@/components/RoleGate";
import { assetDisplay } from "@/lib/assetDisplay";
import { TourFormDialog } from "@/pages/tours/TourFormDialog";
import { api } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const READ = ["super_admin", "admin", "leasing_sales", "ops_dispatch", "accounting", "maintenance"];

// RC10k · Calendar-safe YYYY-MM-DD formatter.
const pad = (n) => (n < 10 ? "0" + n : "" + n);
function iso(d) {
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

// RC10p · ISO date-string validation. Returns true only for a strict
// YYYY-MM-DD literal that round-trips cleanly through Date.
function isValidIsoDate(s) {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

const VIEW_PREF_KEY = "aa_tours_view_pref";
// RC10p · sessionStorage is now a one-way fallback only (read-once if
// URL has no `schedule_start`/`schedule_end` params; writes land in
// the URL from that point forward).
const SS_KEY = "aa_schedule_state_v1";

function defaultDateRange() {
  const today = new Date();
  const end = new Date(today);
  end.setDate(end.getDate() + 14);
  return { start: iso(today), end: iso(end) };
}

/**
 * RC10p · Resolve the authoritative date range for the Schedule page.
 *
 * Priority order (first hit wins):
 *   1. URL `?schedule_start=&schedule_end=` (both present, both valid, end >= start)
 *   2. sessionStorage fallback (read-once legacy state carried over
 *      from RC4; `start`/`end` keys, both valid, end >= start)
 *   3. today → today+14
 *
 * Malformed URL input → fallback to the default range AND emit a
 * console.warn with the malformed value. Does NOT crash.
 */
function resolveInitialRange(params) {
  const urlStart = params.get("schedule_start");
  const urlEnd = params.get("schedule_end");
  if (urlStart || urlEnd) {
    if (isValidIsoDate(urlStart) && isValidIsoDate(urlEnd) && urlStart <= urlEnd) {
      return { start: urlStart, end: urlEnd, source: "url" };
    }
    // eslint-disable-next-line no-console
    console.warn(
      `[SchedulePage] malformed URL date range (start=${urlStart}, end=${urlEnd}) — falling back to default`,
    );
    return { ...defaultDateRange(), source: "fallback_malformed" };
  }
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        isValidIsoDate(parsed?.start) &&
        isValidIsoDate(parsed?.end) &&
        parsed.start <= parsed.end
      ) {
        return { start: parsed.start, end: parsed.end, source: "session" };
      }
    }
  } catch { /* non-fatal */ }
  return { ...defaultDateRange(), source: "default" };
}

export default function SchedulePage() {
  const { can, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params, setParams] = useSearchParams();

  // RC10o — URL-backed Fleet/Drivers tab toggle. Default "fleet".
  const scheduleView = (params.get("schedule_view") === "drivers") ? "drivers" : "fleet";

  // RC10p — URL-backed Fleet/Drivers schedule_start + schedule_end.
  // Resolve once on mount so Back/Forward restores correctly; after
  // that, URL is the authoritative source.
  const initial = useMemo(() => resolveInitialRange(params), []);  // eslint-disable-line react-hooks/exhaustive-deps

  // The derived start/end always come from params (with a defensive
  // fallback). Dependent effects observe start/end directly.
  const rawStart = params.get("schedule_start");
  const rawEnd = params.get("schedule_end");
  const start = (isValidIsoDate(rawStart) && isValidIsoDate(rawEnd) && rawStart <= rawEnd) ? rawStart : initial.start;
  const end = (isValidIsoDate(rawStart) && isValidIsoDate(rawEnd) && rawStart <= rawEnd) ? rawEnd : initial.end;

  // One-time migration: if the URL does NOT already carry the dates,
  // write them so Back navigation works from here on.
  useEffect(() => {
    if (!rawStart || !rawEnd || !isValidIsoDate(rawStart) || !isValidIsoDate(rawEnd) || rawStart > rawEnd) {
      const next = new URLSearchParams(params);
      next.set("schedule_start", start);
      next.set("schedule_end", end);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setScheduleView = (v) => {
    const next = new URLSearchParams(params);
    if (v === "fleet") next.delete("schedule_view");
    else next.set("schedule_view", v);
    setParams(next, { replace: false });
  };
  const setStart = (newStart) => {
    const next = new URLSearchParams(params);
    next.set("schedule_start", newStart);
    // If the new start pushes past end, snap end forward.
    if (newStart > end) next.set("schedule_end", newStart);
    setParams(next, { replace: false });
  };
  const setEnd = (newEnd) => {
    const next = new URLSearchParams(params);
    next.set("schedule_end", newEnd);
    if (newEnd < start) next.set("schedule_start", newEnd);
    setParams(next, { replace: false });
  };

  const isSuperAdmin = user && user.role === "super_admin";
  const [data, setData] = useState(null);
  const [assets, setAssets] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [availabilityWindows, setAvailabilityWindows] = useState([]);
  const [readyToBook, setReadyToBook] = useState([]);
  const [fetchCounts, setFetchCounts] = useState({ assets: 0, drivers: 0 });
  const timelineScrollRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [newTourOpen, setNewTourOpen] = useState(false);
  // RC10q · Fleet-tab inline search. Applies to the Coach + Other
  // Assets group only. Default empty → render ALL rows. Searching
  // filters only the visible rows; the TOTAL count still reflects
  // the unfiltered roster so the operator always knows the full
  // fleet size.
  const [fleetSearch, setFleetSearch] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const canWrite = can && can("tours.create");

  const load = async () => {
    try {
      const d = await scheduleApi.timeline(start, end, "both");
      setData(d);
    } catch { toast.error("Schedule failed"); }
  };

  // RC10p · Fix 3 (G4c, Pass 3 F3f) — TWO-EFFECT SPLIT.
  //
  // Prior pass combined date-dependent AND static-roster fetches
  // under a single `useEffect([start, end])`. The StrictMode guard
  // held on initial mount but ANY real `(start, end)` change
  // legitimately re-fired every call in the effect — including the
  // static roster that should be cached across date changes.
  //
  // Pass 3 splits the responsibilities:
  //   1. `rosterRef` — mount-once, dependency `[]`. Loads /assets,
  //      /drivers, /customers, /tours. Never re-fires on date change.
  //   2. `dateKeyRef` — dependency `[start, end]`. Loads /schedule
  //      and /driver-availability/windows. Fires on each real range
  //      change, idempotent under StrictMode double-mount.
  //
  // Both refs are StrictMode-guarded to short-circuit the second
  // dev-mode invocation. Production builds (no StrictMode) call
  // each effect once per mount — the guard is a no-op there.

  // ---- Effect A: static-roster, mount-once ----
  const rosterRef = useRef(false);
  useEffect(() => {
    if (rosterRef.current) return;
    rosterRef.current = true;
    paginatedFetch(wrapListApi(assetApi.list, { include_archived: false }), { pageSize: 100 })
      .then((res) => {
        setAssets(res.items);
        setFetchCounts((prev) => ({ ...prev, assets: res.items.length }));
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[SchedulePage] asset pagination failed", err);
        toast.error("Could not load the full Fleet roster");
      });
    paginatedFetch(wrapListApi(driverApi.list, { include_archived: false }), { pageSize: 200 })
      .then((res) => {
        setDrivers(res.items);
        setFetchCounts((prev) => ({ ...prev, drivers: res.items.length }));
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[SchedulePage] driver pagination failed", err);
        toast.error("Could not load the full Driver roster");
      });
    customerApi.list({ page_size: 100 }).then((d) => setCustomers(d.results || [])).catch(() => {});
    import("@/lib/api").then(({ api: _api }) =>
      _api.get("/tours?status=ready_to_book&page_size=25")
        .then((r) => setReadyToBook(r.data?.results || [])).catch(() => setReadyToBook([]))
    );
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // ---- Effect B: date-range dependent, re-fires per range ----
  const dateKeyRef = useRef(null);
  useEffect(() => {
    const key = `${start}|${end}`;
    if (dateKeyRef.current === key) return;
    dateKeyRef.current = key;
    load();
    driverAvailabilityApi.windowsBatch({ start, end })
      .then((rows) => setAvailabilityWindows(Array.isArray(rows) ? rows : []))
      .catch(() => setAvailabilityWindows([]));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [start, end]);

  // RC10p · Keep the sessionStorage one-way fallback write path
  // so older browser tabs that still read SS_KEY on hard-reload
  // continue to see a sane range. URL remains authoritative.
  useEffect(() => {
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify({ start, end, scrollY: window.scrollY }));
    } catch { /* non-fatal */ }
  }, [start, end]);

  // RC10k · day column generation using LOCAL date components.
  const days = [];
  if (data) {
    const [sy, sm, sd] = start.split("-").map(Number);
    const [ey, em, ed] = end.split("-").map(Number);
    const s = new Date(sy, sm - 1, sd);
    const e = new Date(ey, em - 1, ed);
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      days.push(iso(new Date(d)));
    }
  }
  const dayCount = days.length || 1;

  const goToTours = (view) => {
    if (view !== "timeline" && view !== "list") return;
    try { window.sessionStorage.setItem(VIEW_PREF_KEY, view); } catch { /* non-fatal */ }
    const search = loc.search || "";
    const sp = new URLSearchParams(search);
    sp.set("view", view);
    nav(`/tours?${sp.toString()}`);
  };

  return (
    <RoleGate allow={READ}>
      <PageShell>
        <PageHeader testid="page-schedule" title="Schedule"
          subtitle={`${start} → ${end}`}
          right={
            <div className="flex flex-wrap items-center gap-2" data-testid="schedule-header-right">
              <div
                className="inline-flex items-center rounded-sm border border-coach-silver overflow-hidden"
                role="tablist"
                aria-label="Tours view mode"
                data-testid="tours-view-mode"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={false}
                  data-testid="tours-view-mode-timeline"
                  onClick={() => goToTours("timeline")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium bg-white text-carbon hover:bg-road-white"
                >
                  <Rows3 size={14} /> Timeline
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={false}
                  data-testid="tours-view-mode-list"
                  onClick={() => goToTours("list")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium border-l border-coach-silver bg-white text-carbon hover:bg-road-white"
                >
                  <ListOrdered size={14} /> List
                </button>
              </div>
              <button
                type="button"
                data-testid="tours-view-schedule"
                aria-current="page"
                aria-pressed="true"
                className="inline-flex items-center gap-1.5 border border-coach-blue bg-coach-blue text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm cursor-default"
              >
                <CalendarDays size={14} /> Schedule
              </button>
              {canWrite && (
                <button
                  data-testid="new-tour-btn"
                  onClick={() => setNewTourOpen(true)}
                  className="inline-flex items-center gap-1.5 bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm"
                >
                  <Plus size={14} /> New Tour
                </button>
              )}
              <input data-testid="schedule-start" type="date" value={start} onChange={(e) => setStart(e.target.value)}
                className="border border-coach-silver rounded-sm px-2 py-1 text-[13px]" />
              <input data-testid="schedule-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                className="border border-coach-silver rounded-sm px-2 py-1 text-[13px]" />
            </div>
          }
        />

        {readyToBook.length > 0 && (
          <div className="bg-touring-lime/15 border border-touring-lime rounded-sm px-3 py-2 mb-3 flex flex-wrap items-center gap-2 text-[12px] text-carbon"
               data-testid="schedule-ready-to-book">
            <span className="uppercase tracking-widest text-[10.5px] text-carbon font-semibold">Ready to Book</span>
            <span className="text-steel">·</span>
            {readyToBook.slice(0, 10).map((t) => (
              <Link key={t.id} to={`/tours/${t.id}`}
                    data-testid={`schedule-rtb-${t.id}`}
                    className="inline-flex items-center gap-1 px-1.5 py-[1px] border border-touring-lime rounded-sm hover:bg-touring-lime/30">
                {t.display_name || t.customer_name || "Tour"} · {t.start_date}
              </Link>
            ))}
            {readyToBook.length > 10 && (
              <span className="text-steel">+ {readyToBook.length - 10} more</span>
            )}
          </div>
        )}

        <div className="bg-white border border-coach-silver rounded-sm px-3 py-2 mb-3 flex flex-wrap items-center gap-3 text-[11px] text-steel" data-testid="schedule-legend">
          {[
            ["Primary", "bg-coach-blue"],
            ["Co-Driver", "bg-[#2A5F96]"],
            ["Fill-In", "bg-deep-road-navy"],
            ["Coverage Needed", "bg-amber-hold"],
            ["Unavailable", "bg-critical-red"],
            ["Hold", "bg-amber-hold"],
            ["Maintenance", "bg-maintenance-orange"],
          ].map(([l, c]) => (
            <span key={l} className="inline-flex items-center gap-1.5">
              <span className={`w-3 h-3 ${c} rounded-sm inline-block`} /> {l}
            </span>
          ))}
        </div>

        {/* RC10o — Fleet / Drivers tab toggle. URL-backed. */}
        <div
          className="inline-flex items-center rounded-sm border border-coach-silver overflow-hidden mb-3"
          role="tablist"
          aria-label="Schedule view"
          data-testid="schedule-view-toggle"
        >
          <button
            type="button"
            role="tab"
            aria-selected={scheduleView === "fleet"}
            data-testid="schedule-view-fleet"
            onClick={() => setScheduleView("fleet")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium ${
              scheduleView === "fleet"
                ? "bg-coach-blue text-white"
                : "bg-white text-carbon hover:bg-road-white"
            }`}
          >
            <Truck size={14} /> Fleet Schedule
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={scheduleView === "drivers"}
            data-testid="schedule-view-drivers"
            onClick={() => setScheduleView("drivers")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium border-l border-coach-silver ${
              scheduleView === "drivers"
                ? "bg-coach-blue text-white"
                : "bg-white text-carbon hover:bg-road-white"
            }`}
          >
            <UserCircle2 size={14} /> Driver Schedule
          </button>
        </div>

        {/* RC10p · set-equality roster-count diagnostics (dev + tests). */}
        <div
          data-testid="schedule-fetch-counts"
          data-asset-count={fetchCounts.assets}
          data-driver-count={fetchCounts.drivers}
          data-availability-window-count={availabilityWindows.length}
          hidden
          aria-hidden="true"
        />

        {isSuperAdmin && data && (((data.asset_orphans || []).length + (data.driver_orphans || []).length) > 0) && (
          <OrphanMaintenanceBanner
            scheduleView={scheduleView}
            data={data}
            onReload={load}
          />
        )}

        {/* RC10q · Fleet / Driver inline search box above the timeline. */}
        <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="schedule-search-row">
          {scheduleView !== "drivers" ? (
            <input
              type="search"
              value={fleetSearch}
              onChange={(e) => setFleetSearch(e.target.value)}
              placeholder="Search Coach or asset name…"
              data-testid="schedule-fleet-search"
              className="border border-coach-silver rounded-sm px-2 py-1 text-[13px] w-72 bg-white"
            />
          ) : (
            <input
              type="search"
              value={driverSearch}
              onChange={(e) => setDriverSearch(e.target.value)}
              placeholder="Search driver name…"
              data-testid="schedule-driver-search"
              className="border border-coach-silver rounded-sm px-2 py-1 text-[13px] w-72 bg-white"
            />
          )}
        </div>

        <TimelineView
          data={data}
          days={days}
          dayCount={dayCount}
          assets={assets}
          drivers={drivers}
          availabilityWindows={availabilityWindows}
          scrollRef={timelineScrollRef}
          onReload={load}
          scheduleView={scheduleView}
          fleetSearch={fleetSearch}
          driverSearch={driverSearch}
        />

        {newTourOpen && (
          <TourFormDialog
            open={newTourOpen}
            customers={customers}
            onClose={() => setNewTourOpen(false)}
            onSaved={() => { setNewTourOpen(false); load(); }}
          />
        )}
      </PageShell>
    </RoleGate>
  );
}

/**
 * RC10p · Resolve the inline role label + compact fallback + bar
 * classname for a Driver assignment. Returns `null` for assignments
 * that render as orphans (orphan path already handles its own label).
 */
function resolveDriverBarMeta(a) {
  if (a?.orphan) return null;
  // Priority: coverage > fill-in > co-driver > primary.
  if (a?.coverage_needed) {
    return {
      long: "Coverage Needed",
      short: "CN",
      cls: "bg-amber-hold text-carbon",
      ariaDesc: "Coverage required",
    };
  }
  if (a?.is_fill_in) {
    return {
      long: "Fill-In",
      short: "FI",
      cls: "bg-deep-road-navy text-white",
      ariaDesc: "Fill-In driver",
    };
  }
  if (a?.role === "co_driver") {
    return {
      long: "Co-Driver",
      short: "CD",
      // RC10p · Fix 2 (G5e) — Co-Driver WCAG AA contrast. Was
      // `bg-coach-blue/70 text-white` (~3.9:1, below 4.5:1 threshold).
      // Now uses a fully-opaque medium-dark coach-blue variant
      // (#2A5F96, computed contrast with white ≈ 5.6:1) plus a thin
      // inset ring for an additional visual cue so Primary (solid
      // coach-blue #1B4F8A) and Co-Driver remain easily distinguishable
      // at a glance even without color-vision assistance. See the
      // RC10p legend swatch update for the same hex.
      cls: "bg-[#2A5F96] text-white ring-1 ring-inset ring-white/60",
      ariaDesc: "Co-Driver",
    };
  }
  return {
    long: "Primary",
    short: "P",
    cls: "bg-coach-blue text-white",
    ariaDesc: "Primary driver",
  };
}

/**
 * RC10p · Inline bar label contents. Narrow bars collapse to the
 * compact code (never color-only). The accessible name retains the
 * long form plus any Tour/client identifying text.
 */
function DriverBarContents({ long, short, tourLabel, widthPx }) {
  // Compact threshold calibrated to the 26px column width — a 2-day
  // bar at colW=26 is ~52px which cannot fit "Co-Driver · <tour>" but
  // can fit "CD".
  const COMPACT_PX = 72;
  const useCompact = widthPx < COMPACT_PX;
  if (useCompact) {
    return (
      <span className="flex items-center justify-center w-full">
        <span className="font-semibold">{short}</span>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 w-full">
      <span className="font-semibold shrink-0">{long}</span>
      {tourLabel ? (
        <>
          <span className="opacity-70">·</span>
          <span className="truncate">{tourLabel}</span>
        </>
      ) : null}
    </span>
  );
}

function TimelineView({
  data,
  days,
  dayCount,
  assets,
  drivers,
  availabilityWindows,
  scrollRef,
  onReload,
  scheduleView,
  fleetSearch = "",
  driverSearch = "",
}) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [confirming, setConfirming] = useState(null);
  const [removing, setRemoving] = useState(false);

  if (!data) return <div className="text-steel text-sm">Loading…</div>;
  const assetsMap = Object.fromEntries(assets.map((a) => [a.id, a]));
  const driversMap = Object.fromEntries(drivers.map((d) => [d.id, d]));  // eslint-disable-line no-unused-vars

  const byAsset = {};
  (data.assignments || []).forEach((a) => {
    (byAsset[a.asset_id] = byAsset[a.asset_id] || []).push(a);
  });
  const byDriver = {};
  (data.driver_assignments || []).forEach((a) => {
    (byDriver[a.driver_id] = byDriver[a.driver_id] || []).push(a);
  });
  const byAssetHolds = {};
  (data.holds || []).forEach((h) => {
    (byAssetHolds[h.asset_id] = byAssetHolds[h.asset_id] || []).push(h);
  });
  // RC10p · availability windows grouped by driver. Only
  // UNAVAILABLE windows render as red blocks on the row.
  const unavailByDriver = {};
  (availabilityWindows || []).forEach((w) => {
    if (w?.status !== "unavailable") return;
    (unavailByDriver[w.driver_id] = unavailByDriver[w.driver_id] || []).push(w);
  });

  const assetIds = Array.from(new Set([...assets.map((a) => a.id), ...Object.keys(byAsset), ...Object.keys(byAssetHolds)]));
  const driverIds = Array.from(new Set([...drivers.map((d) => d.id), ...Object.keys(byDriver)]));  // eslint-disable-line no-unused-vars

  const colW = 26;
  const rowH = 30;

  const tourBar = (a, tour, colorClass, tooltip, testid, labelMeta) => {
    const windowStart = days[0];
    const windowEnd = days[days.length - 1];
    if (!windowStart || !windowEnd) return null;
    if (a.start_date > windowEnd || a.end_date < windowStart) return null;
    const startIdx = days.indexOf(a.start_date);
    const endIdx = days.indexOf(a.end_date);
    const from = startIdx >= 0 ? startIdx : 0;
    const to = endIdx >= 0 ? endIdx : days.length - 1;
    const left = from * colW;
    const width = (to - from + 1) * colW - 2;
    if (a.orphan) {
      return (
        <div
          key={testid}
          data-testid={`${testid}-orphan`}
          data-orphan="true"
          data-missing-tour-id={a.missing_parent_tour_id || ""}
          title={`Orphaned assignment · parent tour missing or archived (${a.missing_parent_tour_id || "unknown"})`}
          className={
            "absolute top-1 h-6 bg-maintenance-orange/80 border border-maintenance-orange " +
            "rounded-sm text-[10px] text-white px-1.5 overflow-hidden whitespace-nowrap " +
            "flex items-center gap-1"
          }
          style={{ left, width }}>
          <AlertTriangle size={10} aria-hidden />
          <span className="truncate">Orphaned assignment</span>
          {isSuperAdmin ? (
            <button
              type="button"
              data-testid={`${testid}-orphan-cleanup`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const label = testid.startsWith("bar-driver-")
                  ? "driver" : "asset";
                setConfirming({
                  resource: label,
                  assn_id: a.id,
                  tooltip,
                });
              }}
              className="ml-auto text-[9px] uppercase tracking-wider underline hover:no-underline">
              Clean up
            </button>
          ) : null}
        </div>
      );
    }
    const safeLabel = tour?.display_name
      || tour?.tour_number
      || a.tour_number
      || a.tour_id;
    // RC10p · Driver bars use the inline role label contract. Asset
    // bars keep their long tour-label text (coach-blue, no role).
    const inlineLabel = labelMeta ? (
      <DriverBarContents long={labelMeta.long} short={labelMeta.short} tourLabel={safeLabel} widthPx={width} />
    ) : (
      <span className="truncate">{tooltip}</span>
    );
    const ariaLabel = labelMeta
      ? `${labelMeta.long}: ${safeLabel}`
      : `Open tour: ${safeLabel}`;
    return (
      <Link
        key={testid}
        to={`/tours/${a.tour_id}`}
        data-testid={testid}
        data-tour-id={a.tour_id}
        data-role-label={labelMeta ? labelMeta.long : undefined}
        title={labelMeta ? `${labelMeta.long} · ${safeLabel}` : tooltip}
        role="link"
        aria-label={ariaLabel}
        onKeyDown={(e) => {
          if (e.key === " " || e.code === "Space") {
            e.preventDefault();
            e.currentTarget.click();
          }
        }}
        className={
          `absolute top-1 h-6 ${colorClass} rounded-sm text-[10px] ` +
          "px-1.5 overflow-hidden whitespace-nowrap flex items-center cursor-pointer " +
          "hover:brightness-110 focus:outline-none focus-visible:ring-2 " +
          "focus-visible:ring-touring-lime focus-visible:ring-offset-1 " +
          "focus-visible:ring-offset-carbon"
        }
        style={{ left, width }}>
        {inlineLabel}
      </Link>
    );
  };

  const staticBar = (startDate, endDate, colorClass, tooltip, testid, extraAttrs = {}, shortLabel = null) => {
    const windowStart = days[0];
    const windowEnd = days[days.length - 1];
    if (!windowStart || !windowEnd) return null;
    if (startDate > windowEnd || endDate < windowStart) return null;
    const startIdx = days.indexOf(startDate);
    const endIdx = days.indexOf(endDate);
    const from = startIdx >= 0 ? startIdx : 0;
    const to = endIdx >= 0 ? endIdx : days.length - 1;
    const left = from * colW;
    const width = (to - from + 1) * colW - 2;
    // RC10p · Fix 1 (G5c) — narrow-bar compact fallback. Uses the
    // SAME <72px threshold applied to Driver Primary/Co-Driver/Fill-
    // In/Coverage-Needed bars so the inline-label contract is uniform
    // across every bar type. `aria-label` + `title` retain the full
    // long form (`Unavailable`, `maintenance`, etc.) regardless of
    // width so screen readers and WCAG scanners always see the full
    // string.
    const COMPACT_PX = 72;
    const inlineText = (shortLabel && width < COMPACT_PX) ? shortLabel : tooltip;
    const centerCompact = (shortLabel && width < COMPACT_PX)
      ? "justify-center"
      : "";
    return (
      <div data-testid={testid} title={tooltip}
        aria-label={tooltip}
        className={`absolute top-1 h-6 ${colorClass} rounded-sm text-[10px] text-white px-1.5 overflow-hidden whitespace-nowrap flex items-center ${centerCompact} font-semibold`}
        style={{ left, width }}
        {...extraAttrs}>
        {inlineText}
      </div>
    );
  };

  const Row = ({ label, bars, testid }) => (
    <div className="flex border-b border-coach-silver/50" style={{ height: rowH }} data-testid={testid}>
      <div className="w-40 shrink-0 text-[12px] pl-3 py-1.5 text-carbon bg-road-white border-r border-coach-silver">{label}</div>
      <div className="relative" style={{ width: dayCount * colW, minWidth: dayCount * colW }}>{bars}</div>
    </div>
  );

  // RC10q · Fleet set-equality — Coach rows derived from the
  // canonical `schedule_category` catalog classification (replaces
  // the RC10p substring predicate). The authoritative set below is
  // the DOM-rendered Coach row set; tests assert against this via
  // the data-coach-ids attribute AND against the server-computed
  // `coach_resource_ids` field on the /api/schedule response for
  // the A=B=C invariant.
  const activeCoachAssets = assets.filter((a) => a && !a.is_archived && _isCoach(a));
  const activeOtherAssets = assets.filter(
    (a) => a && !a.is_archived && !_isCoach(a) && !_isHidden(a),
  );
  const activeCoachIds = activeCoachAssets.map((a) => a.id);

  // RC10q · search is case-insensitive substring match against unit
  // name and asset_type code. The FULL count is preserved so the
  // operator always sees `({filtered} of {total})`.
  const _searchMatch = (asset, needle) => {
    if (!needle) return true;
    const n = needle.toLowerCase();
    return (
      (asset.unit_name || "").toLowerCase().includes(n) ||
      (asset.asset_type || "").toLowerCase().includes(n) ||
      (asset.fleet_number || "").toLowerCase().includes(n)
    );
  };
  const visibleCoachAssets = activeCoachAssets.filter((a) => _searchMatch(a, fleetSearch));
  const visibleOtherAssets = activeOtherAssets.filter((a) => _searchMatch(a, fleetSearch));
  const visibleDrivers = drivers
    .filter((d) => !d.is_archived)
    .filter((d) => {
      if (!driverSearch) return true;
      const n = driverSearch.toLowerCase();
      return (
        (d.display_name || "").toLowerCase().includes(n) ||
        (d.driver_number || "").toLowerCase().includes(n)
      );
    });

  const _headerCount = (filtered, total) => (
    filtered === total ? `(${total})` : `(${filtered} of ${total})`
  );

  return (
    <div ref={scrollRef} className="bg-white border border-coach-silver rounded-md overflow-auto" data-testid="schedule-timeline"
         data-coach-ids={activeCoachIds.join(",")}
         data-coach-total={activeCoachAssets.length}
         data-coach-visible={visibleCoachAssets.length}
         data-other-total={activeOtherAssets.length}
         data-driver-total={drivers.filter((d) => !d.is_archived).length}>
      <div className="flex sticky top-0 z-10 bg-white border-b border-coach-silver">
        <div className="w-40 shrink-0 text-[10.5px] uppercase tracking-widest text-steel px-3 py-1.5 bg-road-white border-r border-coach-silver">Resource</div>
        <div className="flex" style={{ minWidth: dayCount * colW }}>
          {days.map((d) => (
            <div key={d} style={{ width: colW }}
              className="text-[10px] text-steel text-center border-r border-coach-silver/50 py-1.5">
              {d.slice(-2)}
            </div>
          ))}
        </div>
      </div>

      {scheduleView !== "drivers" && (
        <>
          <div
            className="text-[10.5px] uppercase tracking-widest text-steel px-3 py-1 bg-road-white border-b border-coach-silver flex items-center gap-2"
            data-testid="schedule-group-coaches"
            data-coach-count={activeCoachAssets.length}
          >
            <span>Coaches</span>
            <span className="text-carbon normal-case tracking-normal text-[11px] font-semibold" data-testid="coaches-header-count">
              {_headerCount(visibleCoachAssets.length, activeCoachAssets.length)}
            </span>
          </div>
          {visibleCoachAssets.map((asset) => {
            const aid = asset.id;
            const bars = [];
            (byAsset[aid] || []).forEach((a) => {
              const t = data.tours[a.tour_id] || {};
              const cls = a.override_reason ? "bg-critical-red text-white" : "bg-coach-blue text-white";
              bars.push(tourBar(a, t, cls, `${t.display_name || "Tour"}`, `bar-asset-${a.id}`));
            });
            (byAssetHolds[aid] || []).forEach((h) => {
              bars.push(
                <React.Fragment key={`hold-${h.id}`}>
                  {staticBar(h.start_date, h.end_date, "bg-amber-hold", `${h.hold_level} hold`, `bar-hold-${h.id}`)}
                </React.Fragment>
              );
            });
            if (["maintenance", "shop", "out_of_service"].includes(asset.operational_status)) {
              bars.push(
                <React.Fragment key={`maint-${asset.id}`}>
                  {staticBar(days[0], days[days.length - 1], "bg-maintenance-orange", `${asset.operational_status}`, `bar-maint-${asset.id}`)}
                </React.Fragment>
              );
            }
            // RC10q · "No Tour Assigned" inline label when the Coach
            // has zero assignment/hold/maintenance bars in the
            // visible range. Preserves the resource row so the
            // operator sees the full fleet.
            if (bars.length === 0) {
              bars.push(
                <div
                  key={`no-tour-${aid}`}
                  data-testid={`bar-no-tour-${aid}`}
                  data-no-tour-assigned="true"
                  className="absolute top-1 h-6 text-[10.5px] text-steel px-2 flex items-center italic"
                  style={{ left: 0 }}
                  aria-label={`${asset.unit_name || aid}: No Tour Assigned`}
                  title="No Tour Assigned"
                >
                  No Tour Assigned
                </div>
              );
            }
            return <Row key={`asset-row-${aid}`} label={assetDisplay(asset) || aid.slice(0, 8)} bars={bars} testid={`schedule-asset-row-${aid}`} />;
          })}
          <div
            className="text-[10.5px] uppercase tracking-widest text-steel px-3 py-1 bg-road-white border-b border-coach-silver mt-1 flex items-center gap-2"
            data-testid="schedule-group-other-assets"
            data-other-count={activeOtherAssets.length}
          >
            <span>Other Assets</span>
            <span className="text-carbon normal-case tracking-normal text-[11px] font-semibold" data-testid="other-assets-header-count">
              {_headerCount(visibleOtherAssets.length, activeOtherAssets.length)}
            </span>
          </div>
          {visibleOtherAssets.map((asset) => {
            const aid = asset.id;
            const bars = [];
            (byAsset[aid] || []).forEach((a) => {
              const t = data.tours[a.tour_id] || {};
              const cls = a.override_reason ? "bg-critical-red text-white" : "bg-coach-blue text-white";
              bars.push(tourBar(a, t, cls, `${t.display_name || "Tour"}`, `bar-asset-${a.id}`));
            });
            if (bars.length === 0) {
              bars.push(
                <div
                  key={`no-tour-${aid}`}
                  data-testid={`bar-no-tour-${aid}`}
                  data-no-tour-assigned="true"
                  className="absolute top-1 h-6 text-[10.5px] text-steel px-2 flex items-center italic"
                  style={{ left: 0 }}
                  aria-label={`${asset.unit_name || aid}: No Tour Assigned`}
                  title="No Tour Assigned"
                >
                  No Tour Assigned
                </div>
              );
            }
            return <Row key={`asset-row-${aid}`} label={assetDisplay(asset) || aid.slice(0, 8)} bars={bars} testid={`schedule-asset-row-${aid}`} />;
          })}
        </>
      )}

      {scheduleView === "drivers" && (
        <>
          <div
            className="text-[10.5px] uppercase tracking-widest text-steel px-3 py-1 bg-road-white border-b border-coach-silver flex items-center gap-2"
            data-testid="schedule-group-drivers"
            data-driver-count={drivers.filter((d) => !d.is_archived).length}
          >
            <span>Drivers</span>
            <span className="text-carbon normal-case tracking-normal text-[11px] font-semibold" data-testid="drivers-header-count">
              {_headerCount(visibleDrivers.length, drivers.filter((d) => !d.is_archived).length)}
            </span>
          </div>
          {visibleDrivers.map((driver) => {
            const did = driver.id;
            const bars = [];
            // RC10p · Unavailable blocks render first (background
            // layer); role-tagged assignment bars render on top.
            // RC10p · Pass 4 (S8) — Unique React key resilient to
            // duplicate (driver_id, start_date, end_date) tuples
            // present in some Preview test-data seeds (V05 fill-in
            // seeds leak 2–5 identical windows per driver). Prefer
            // the backend-stable `w.id` when present; fall back to
            // the map index so keys are guaranteed unique within
            // this `.map()` scope even if the batched endpoint ever
            // omits `id`. Date fragments retained for human-
            // readable React DevTools inspection.
            (unavailByDriver[did] || []).forEach((w, widx) => {
              const uniq = w.id ?? widx;
              bars.push(
                <React.Fragment key={`unavail-${did}-${uniq}-${w.start_date}-${w.end_date}`}>
                  {staticBar(
                    w.start_date,
                    w.end_date,
                    "bg-critical-red",
                    "Unavailable",
                    `bar-driver-${did}-unavailable-${uniq}-${w.start_date}`,
                    { "data-unavailable-reason": w.reason || "", "data-short-label": "UN" },
                    "UN",
                  )}
                </React.Fragment>
              );
            });
            (byDriver[did] || []).forEach((a) => {
              const t = data.tours[a.tour_id] || {};
              const meta = resolveDriverBarMeta(a);
              if (!meta) {
                // orphan path — pass null label so tourBar's orphan branch handles it.
                bars.push(tourBar(a, t, "bg-coach-blue text-white", `${t.display_name || "Tour"}`, `bar-driver-${a.id}`, null));
                return;
              }
              bars.push(tourBar(a, t, meta.cls, `${t.display_name || "Tour"} · ${meta.long}`, `bar-driver-${a.id}`, meta));
            });
            return (
              <Row
                key={`driver-row-${did}`}
                testid={`schedule-driver-row-${did}`}
                label={
                  <Link to={`/drivers/${did}`} className="text-coach-blue hover:underline" data-testid={`driver-row-link-${did}`}>
                    {driver.display_name || did.slice(0, 8)}
                  </Link>
                }
                bars={bars}
              />
            );
          })}
        </>
      )}

      <AlertDialog open={!!confirming} onOpenChange={(o) => { if (!o && !removing) setConfirming(null); }}>
        <AlertDialogContent data-testid="orphan-cleanup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="orphan-cleanup-title">
              Remove orphaned schedule assignment?
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="orphan-cleanup-body">
              This removes the orphaned schedule assignment only. It does not delete the driver or vehicle.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="orphan-cleanup-cancel" disabled={removing}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="orphan-cleanup-confirm"
              disabled={removing}
              onClick={async () => {
                if (!confirming) return;
                setRemoving(true);
                try {
                  if (confirming.resource === "driver") {
                    await scheduleApi.removeOrphanDriverAssignment(confirming.assn_id);
                  } else {
                    await scheduleApi.removeOrphanAssetAssignment(confirming.assn_id);
                  }
                  toast.success("Orphaned schedule assignment removed.");
                  setConfirming(null);
                  if (typeof onReload === "function") await onReload();
                } catch (err) {
                  toast.error(err?.response?.data?.detail?.message
                    || err?.response?.data?.detail
                    || "Unable to remove orphan assignment.");
                } finally {
                  setRemoving(false);
                }
              }}>
              {removing ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// RC10q · Replace the old substring predicate with a stable
// catalog-driven classifier. The /api/assets endpoint now enriches
// each item with `schedule_category`. Falls back to the legacy
// substring match ONLY when `schedule_category` is absent (defensive
// — never fires on an RC10q+ backend).
function _resolveCategory(asset) {
  if (!asset) return "other";
  const cat = asset.schedule_category;
  if (cat === "coach" || cat === "other" || cat === "hidden") return cat;
  // Legacy fallback — RC10p predicate preserved for forward-compat
  // only. On an RC10q backend this branch is never taken.
  if (asset.asset_type && String(asset.asset_type).toLowerCase().includes("coach")) {
    return "coach";
  }
  return "other";
}

function _isCoach(asset) {
  return _resolveCategory(asset) === "coach";
}

function _isHidden(asset) {
  return _resolveCategory(asset) === "hidden";
}

function OrphanMaintenanceBanner({ scheduleView, data, onReload }) {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const assetOrphans = data.asset_orphans || [];
  const driverOrphans = data.driver_orphans || [];
  const list = (scheduleView === "drivers" ? driverOrphans : assetOrphans);
  const count = list.length;
  if (count === 0) return null;
  const remove = async (row) => {
    setBusyId(row.id);
    try {
      const path = (scheduleView === "drivers")
        ? `/schedule/orphan-assignments/driver/${row.id}`
        : `/schedule/orphan-assignments/asset/${row.id}`;
      await api.delete(path);
      toast.success("Orphan assignment removed");
      setConfirmTarget(null);
      if (typeof onReload === "function") await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Unable to remove orphan assignment.");
    } finally {
      setBusyId(null);
    }
  };
  return (
    <div data-testid="schedule-orphan-maintenance-banner"
         data-orphan-count={count}
         className="border border-amber-hold bg-amber-hold/15 rounded-sm px-3 py-2.5 mb-3 flex flex-wrap items-start gap-2">
      <AlertTriangle size={16} className="text-amber-hold mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-carbon font-medium" data-testid="schedule-orphan-banner-wording">
          Schedule maintenance required — {count} assignment(s) reference missing or archived Fleet
          {scheduleView === "drivers" ? " drivers" : " assets"}.
        </div>
        <button type="button" data-testid="schedule-orphan-review-btn"
                onClick={() => setOpen(true)}
                className="mt-1 inline-flex items-center text-[12px] text-coach-blue hover:underline">
          Review
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-carbon/70 flex items-center justify-center px-4"
             data-testid="schedule-orphan-review-panel"
             onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-md w-full max-w-3xl border border-coach-silver shadow-xl max-h-[80vh] flex flex-col">
            <div className="bg-carbon text-white px-4 py-3 flex items-center justify-between">
              <div className="text-[14px] font-medium">
                Orphan {scheduleView === "drivers" ? "Driver" : "Asset"} Assignments
              </div>
              <button type="button" onClick={() => setOpen(false)}
                      data-testid="schedule-orphan-panel-close"
                      className="text-coach-silver hover:text-white text-[12px]">Close</button>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
                  <tr>
                    <th className="px-3 py-2 text-left">Historical label</th>
                    <th className="px-3 py-2 text-left">Assignment ID</th>
                    <th className="px-3 py-2 text-left">Stored {scheduleView === "drivers" ? "driver" : "asset"} ID</th>
                    <th className="px-3 py-2 text-left">Tour</th>
                    <th className="px-3 py-2 text-left">Dates</th>
                    <th className="px-3 py-2 text-left">Reason</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((row) => {
                    const storedId = scheduleView === "drivers" ? row.driver_id : row.asset_id;
                    const label = row.denormalized_label || row.display_name || "(Missing)";
                    const tourId = row.tour_id;
                    const tour = (data.tours || {})[tourId];
                    const reason = (row.orphan_reason || "").replace("ghost_resource:", "") || "invalid";
                    return (
                      <tr key={row.id} className="border-t border-coach-silver/60"
                          data-testid={`schedule-orphan-row-${row.id}`}>
                        <td className="px-3 py-2 text-carbon">{label}</td>
                        <td className="px-3 py-2 font-mono text-[10.5px]">{row.id.slice(0, 8)}</td>
                        <td className="px-3 py-2 font-mono text-[10.5px]">{(storedId || "").slice(0, 8) || "—"}</td>
                        <td className="px-3 py-2">{tour?.display_name || (tourId ? tourId.slice(0, 8) : "—")}</td>
                        <td className="px-3 py-2 text-steel">{row.start_date} \u2192 {row.end_date}</td>
                        <td className="px-3 py-2 uppercase tracking-widest text-[10px] text-amber-hold">{reason}</td>
                        <td className="px-3 py-2 text-right">
                          <button type="button" disabled={busyId === row.id}
                                  onClick={() => setConfirmTarget(row)}
                                  data-testid={`schedule-orphan-remove-${row.id}`}
                                  className="px-2 py-1 text-[11px] border border-critical-red text-critical-red rounded-sm hover:bg-critical-red/10 disabled:opacity-40">
                            Remove Orphan Assignment
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!confirmTarget} onOpenChange={(o) => { if (!o && busyId === null) setConfirmTarget(null); }}>
        <AlertDialogContent data-testid="schedule-orphan-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove orphan schedule assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes only the invalid assignment row. The Tour, Fleet asset, Driver,
              Contract, and Pickup Sheet records are not touched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busyId !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="schedule-orphan-confirm-remove"
                               disabled={busyId !== null}
                               onClick={async () => { if (confirmTarget) await remove(confirmTarget); }}>
              {busyId ? "Removing\u2026" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
